import { useState } from "react";

/**
 * Shared dismiss-with-reason modal for CoordTask rows.
 *
 * v1.77 introduced this UX on the UserDashboard MY ACCOUNTABILITY widget;
 * v1.79 extracts it so the same flow works on the Task Registry detail
 * drawer, the Marketing Response detail view, and any other surface that
 * displays an individual task assigned to the viewer.
 *
 * Props:
 *   open: boolean — whether the modal is rendered
 *   task: { task_id, task_title } | { id, title } — the task to dismiss.
 *     Accepts either accountability-shape or registry-history-shape rows.
 *   onClose: () => void — fired when the user cancels or after success
 *   onDismissed: (taskId) => void — fired after a successful dismiss so
 *     the parent can locally hide the task without a refetch
 *
 * Backend contract: POST /api/coord-tasks/{id}/dismiss with body
 * { reason: string (min 10 chars) }. Permissions are enforced server-side
 * (assignee / creator / admin).
 */
export default function TaskDismissModal({ open, task, onClose, onDismissed }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!open || !task) return null;

  const taskId = task.task_id || task.id;
  const title = task.task_title || task.title || "this task";

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/coord-tasks/${taskId}/dismiss`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: "Dismissed by user." }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail || `HTTP ${res.status}`);
        setSubmitting(false);
        return;
      }
      if (onDismissed) onDismissed(taskId);
      onClose();
    } catch (err) {
      setError(String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-md p-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100">
          Dismiss task
        </h3>
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
          Are you sure you want to dismiss <span className="font-medium">{title}</span>?
        </p>
        {error && <div className="mt-3 text-xs text-red-600">{error}</div>}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="px-3 py-1.5 text-xs rounded border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1.5 text-xs rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40"
          >
            {submitting ? "Dismissing…" : "Dismiss"}
          </button>
        </div>
      </div>
    </div>
  );
}
